import React, { useState, useEffect } from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import {
    Box, Flex, Text, Button, Image
} from 'components/ui';
import localeUtils from 'utils/LanguageLocale';

import SmsTextOptInAlertBannerEN from 'components/SmsTextOptInAlertBanner/locales/SmsTextOptInAlertBanner_en_US';
import SmsTextOptInAlertBannerFR from 'components/SmsTextOptInAlertBanner/locales/SmsTextOptInAlertBanner_fr_CA';

import SmsOptInModal from 'components/SmsOptInModal';
import processEvent from 'analytics/processEvent';
import anaConsts from 'analytics/constants';
import anaUtils from 'analytics/utils';

const SmsTextOptInAlertBanner = ({
    marginBottom, smsTextOptInPromos, isUserOptedIn, bannerOrigin, ...rest
}) => {
    // Fallback Default image and label for the banner.
    const getText = localeUtils.getLocalisedDataFromResource(SmsTextOptInAlertBannerEN, SmsTextOptInAlertBannerFR);
    const imgSrc = '/img/ufe/sms-opt-in-modal-icon.svg';

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [shouldOpenModal] = useState(() => {
        const confSettings = typeof Sephora !== 'undefined' ? Sephora?.configurationSettings : null;

        switch (bannerOrigin || '') {
            case 'OrderDetail':
                return (confSettings?.sms?.orderDetailsOptInModal?.isEnabled || false) && !isUserOptedIn;
            case 'OrderConfirmation':
                return (confSettings?.sms?.orderConfirmationOptInModal?.isEnabled || false) && !isUserOptedIn;
            default:
                return false;
        }
    });

    useEffect(() => {
        if (isModalOpen) {
            const pageName = anaUtils.getPageInfo()?.pageName || '';

            processEvent.process(anaConsts.ASYNC_PAGE_LOAD, {
                data: {
                    linkData: 'sms-opt-in-modal:view',
                    pageName
                }
            });
        }
    }, [isModalOpen]);

    const handleContinue = () => {
        // Make API call to subscribe user
        setIsModalOpen(false);
    };

    const handleDismiss = params => {
        const { formSubmittedSuccessfully } = params || {};

        if (!formSubmittedSuccessfully) {
            processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
                data: {
                    linkName: 'sms-opt-in-modal:close',
                    actionInfo: 'sms-opt-in-modal:abandon',
                    pageDetail: 'abandon'
                }
            });
        }

        setIsModalOpen(false);
    };

    const getBannerLabel = () => {
        const fallBackBannerLabel = getText('description');

        if (smsTextOptInPromos && smsTextOptInPromos.promos && smsTextOptInPromos.promos.length > 0) {
            const latestPromo = smsTextOptInPromos.promos[0] || ''; // Assuming the latest promo is the first one in the array

            return latestPromo !== '' ? latestPromo.promoBannerDescription : fallBackBannerLabel; // Use the promo title if available
        }

        return fallBackBannerLabel;
    };

    const handleKeyDown = event => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setIsModalOpen(true);
        }
    };

    return !shouldOpenModal ? null : (
        <>
            <Box
                backgroundColor='lightBlue'
                borderRadius={2}
                padding={4}
                marginBottom={marginBottom || 4}
                display='flex'
                flexDirection={['column', 'row']}
                alignItems={['flex-start', 'center']}
                justifyContent='space-between'
                gap={3}
                width='100%'
                onClick={() => setIsModalOpen(true)}
                onKeyDown={handleKeyDown}
                role='button'
                aria-label={getText('buttonText')}
                data-at={Sephora.debug.dataAt('sms_text_alert_banner')}
                {...rest}
            >
                <Flex
                    alignItems='center'
                    gap={3}
                    flex={1}
                >
                    <Box
                        width={48}
                        height={48}
                        flexShrink={0}
                        borderRadius={2}
                        display='flex'
                        alignItems='center'
                        justifyContent='center'
                        position='relative'
                    >
                        <Image src={imgSrc} />
                    </Box>
                    <Box flex={1}>
                        <Text
                            is='h3'
                            fontSize='md'
                            fontWeight='bold'
                            marginBottom={0}
                            data-at={Sephora.debug.dataAt('sms_text_alert_banner_title')}
                        >
                            {getText('title')}
                        </Text>
                        <Text
                            fontSize='md'
                            color='black'
                            data-at={Sephora.debug.dataAt('sms_text_alert_banner_description')}
                        >
                            {getBannerLabel()}
                        </Text>
                    </Box>
                </Flex>
                <Button
                    variant='secondary'
                    onClick={e => {
                        e.stopPropagation();
                        setIsModalOpen(true);
                    }}
                    width={['100%', 'auto']}
                    whiteSpace='nowrap'
                    backgroundColor='white'
                    data-at={Sephora.debug.dataAt('sms_text_alert_banner_button')}
                >
                    {getText('buttonText')}
                </Button>
            </Box>
            <SmsOptInModal
                smsTextOptInPromos={smsTextOptInPromos}
                isOpen={isModalOpen}
                onDismiss={handleDismiss}
                onContinue={handleContinue}
                shouldOpenModal={shouldOpenModal}
            />
        </>
    );
};

export default wrapFunctionalComponent(SmsTextOptInAlertBanner, 'SmsTextOptInAlertBanner');
